import { BrandKey } from '../brand-config';

export interface AutomationLogEntry {
  timestamp: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

export interface AutomationMetrics {
  runsToday: number;
  runsThisWeek: number;
  successRate: number;
  avgExecutionTime: string;
}

export interface Automation {
  id: string;
  name: string;
  description: string;
  category: string;
  status: 'active' | 'paused' | 'development' | 'error';
  brands: string[];
  triggers: string[];
  actions: string[];
  lastRun?: string;
  nextRun?: string;
  metrics?: AutomationMetrics;
  logs?: AutomationLogEntry[];
  version: string;
  createdAt: string;
  updatedAt: string;
}

const baseAutomations: Automation[] = [
    {
      id: 'review-sync-meta',
      name: 'Meta Reviews Sync',
      description: 'Automatically syncs reviews from Meta (Facebook/Instagram) ads manager and extracts creative highlights',
      category: 'Review Management',
      status: 'active',
      brands: ['mars-men', 'hollow-socks'],
      triggers: ['Daily at 2 AM EST', 'Manual trigger'],
      actions: ['Fetch Meta reviews', 'Process with AI', 'Store in DynamoDB', 'Generate highlights'],
      lastRun: '2024-01-21T07:00:00Z',
      nextRun: '2024-01-22T07:00:00Z',
      metrics: {
        runsToday: 3,
        runsThisWeek: 21,
        successRate: 98.5,
        avgExecutionTime: '45s',
      },
      logs: [
        { timestamp: '2024-01-21T07:00:00Z', message: 'Successfully synced 145 reviews', type: 'success' },
        { timestamp: '2024-01-21T02:00:00Z', message: 'Successfully synced 89 reviews', type: 'success' },
        { timestamp: '2024-01-20T19:00:00Z', message: 'Successfully synced 203 reviews', type: 'success' },
      ],
      version: '1.2.0',
      createdAt: '2024-01-10T10:00:00Z',
      updatedAt: '2024-01-20T15:30:00Z',
    },
    {
      id: 'review-sync-junip',
      name: 'Junip Reviews Sync',
      description: 'Syncs customer reviews from Junip platform and processes them for creative insights',
      category: 'Review Management',
      status: 'active',
      brands: ['mars-men', 'hollow-socks'],
      triggers: ['Every 6 hours', 'Webhook on new review'],
      actions: ['Fetch Junip reviews', 'Extract highlights', 'Update statistics', 'Notify team'],
      lastRun: '2024-01-21T06:00:00Z',
      nextRun: '2024-01-21T12:00:00Z',
      metrics: {
        runsToday: 4,
        runsThisWeek: 28,
        successRate: 99.2,
        avgExecutionTime: '30s',
      },
      version: '1.1.0',
      createdAt: '2024-01-08T10:00:00Z',
      updatedAt: '2024-01-18T12:00:00Z',
    },
    {
      id: 'shopify-sync',
      name: 'Shopify Reviews Import',
      description: 'Imports and processes product reviews directly from Shopify stores',
      category: 'Review Management',
      status: 'active',
      brands: ['mars-men', 'hollow-socks'],
      triggers: ['Daily at 3 AM EST'],
      actions: ['Connect to Shopify API', 'Fetch new reviews', 'Process and categorize', 'Generate reports'],
      lastRun: '2024-01-21T08:00:00Z',
      nextRun: '2024-01-22T08:00:00Z',
      metrics: {
        runsToday: 1,
        runsThisWeek: 7,
        successRate: 100,
        avgExecutionTime: '2m 15s',
      },
      version: '1.0.0',
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-20T10:00:00Z',
    },
    {
      id: 'ad-copy-generator',
      name: 'AI Ad Copy Generator',
      description: 'Generates ad copy variations using customer review language and proven hooks',
      category: 'Content Generation',
      status: 'development',
      brands: ['mars-men'],
      triggers: ['On-demand', 'Weekly batch'],
      actions: ['Analyze top reviews', 'Generate copy variations', 'Score effectiveness', 'Export to Meta'],
      metrics: {
        runsToday: 0,
        runsThisWeek: 2,
        successRate: 85,
        avgExecutionTime: '1m 30s',
      },
      version: '0.8.0',
      createdAt: '2024-01-18T10:00:00Z',
      updatedAt: '2024-01-21T09:00:00Z',
    },
    {
      id: 'inventory-alerts',
      name: 'Low Inventory Alerts',
      description: 'Monitors inventory levels and sends alerts when stock is running low',
      category: 'Inventory Management',
      status: 'active',
      brands: ['mars-men', 'hollow-socks'],
      triggers: ['Every hour', 'Threshold reached'],
      actions: ['Check inventory levels', 'Compare to thresholds', 'Send Slack alerts', 'Create reorder report'],
      lastRun: '2024-01-21T09:00:00Z',
      nextRun: '2024-01-21T10:00:00Z',
      metrics: {
        runsToday: 9,
        runsThisWeek: 168,
        successRate: 100,
        avgExecutionTime: '10s',
      },
      version: '2.0.1',
      createdAt: '2023-12-01T10:00:00Z',
      updatedAt: '2024-01-15T14:00:00Z',
    },
    {
      id: 'email-campaign-automation',
      name: 'Email Campaign Automation',
      description: 'Automates email campaigns based on customer behavior and purchase patterns',
      category: 'Marketing',
      status: 'paused',
      brands: ['hollow-socks'],
      triggers: ['Customer action', 'Scheduled campaigns'],
      actions: ['Segment customers', 'Personalize content', 'Send emails', 'Track engagement'],
      lastRun: '2024-01-19T10:00:00Z',
      metrics: {
        runsToday: 0,
        runsThisWeek: 0,
        successRate: 92.3,
        avgExecutionTime: '5m',
      },
      version: '1.5.2',
      createdAt: '2023-11-15T10:00:00Z',
      updatedAt: '2024-01-19T10:00:00Z',
    },
    {
      id: 'social-media-scheduler',
      name: 'Social Media Post Scheduler',
      description: 'Schedules and publishes social media content across platforms using review highlights',
      category: 'Marketing',
      status: 'development',
      brands: ['mars-men', 'hollow-socks'],
      triggers: ['Content calendar', 'Manual scheduling'],
      actions: ['Generate post content', 'Create visuals', 'Schedule posts', 'Track performance'],
      version: '0.5.0',
      createdAt: '2024-01-20T10:00:00Z',
      updatedAt: '2024-01-21T08:00:00Z',
    },
    {
      id: 'customer-service-bot',
      name: 'Customer Service AI Bot',
      description: 'AI-powered customer service bot that handles common inquiries and support tickets',
      category: 'Customer Support',
      status: 'active',
      brands: ['mars-men', 'hollow-socks'],
      triggers: ['New support ticket', 'Live chat message'],
      actions: ['Analyze inquiry', 'Generate response', 'Escalate if needed', 'Log interaction'],
      lastRun: '2024-01-21T09:30:00Z',
      metrics: {
        runsToday: 145,
        runsThisWeek: 980,
        successRate: 88.5,
        avgExecutionTime: '3s',
      },
      version: '3.1.0',
      createdAt: '2023-10-01T10:00:00Z',
      updatedAt: '2024-01-18T16:00:00Z',
    },
];

const cloneAutomation = (automation: Automation): Automation => ({
  ...automation,
  brands: [...automation.brands],
  triggers: [...automation.triggers],
  actions: [...automation.actions],
  metrics: automation.metrics ? { ...automation.metrics } : undefined,
  logs: automation.logs ? automation.logs.map((log) => ({ ...log })) : undefined,
});

const automateBrandMapping: Record<BrandKey, string> = {
  marsmen: 'mars-men',
  hollow: 'hollow-socks',
  easystreet: 'easy-street',
};

const matchesBrand = (automation: Automation, brandToken: string) =>
  brandToken === 'easy-street' ||
  automation.brands.includes(brandToken) ||
  automation.brands.includes('easy-street');

const dataByBrand: Record<BrandKey, Automation[]> = {
  marsmen: baseAutomations
    .filter((automation) => matchesBrand(automation, automateBrandMapping.marsmen))
    .map(cloneAutomation),
  hollow: baseAutomations
    .filter((automation) => matchesBrand(automation, automateBrandMapping.hollow))
    .map(cloneAutomation),
};

export function getAutomationsForBrand(brand: BrandKey): Automation[] {
  return dataByBrand[brand].map(cloneAutomation);
}

export function getAutomationCategories(brand: BrandKey): string[] {
  const categories = new Set<string>();
  getAutomationsForBrand(brand).forEach((automation) => categories.add(automation.category));
  return Array.from(categories);
}
